/**
 * Single source of truth for xVT feature access, shared by the two choke points that gate it:
 * the route guard (`XptXvtAccessWrapper` in src/app/XPTBusinessGroupWrapper.tsx) and the
 * "xVT Planning" side-nav section (`buildBusinessGroupNavigationItems` in
 * src/features/business-group/business-group-side-navigation/utils/NavigationHelpers.ts).
 * Lives in src/utils/ so the app layer can use it without importing feature internals.
 */

import { BUSINESS_GROUP_CODES } from 'src/constants/business-group-constants';
import ENVIRONMENT_VARIABLES from 'src/constants/environment-variables';
import { STAGES } from 'src/constants/generic-constants';
import { XVT_COST_CENTERS } from 'src/constants/xvt-constants';

export interface XvtAccessParams {
  /** `data_classification_short_description` of the business group the user is currently in. */
  currentBusinessGroupCode: string;
  /** Developer LDAP group membership from `useAuth()` — per-user, so it holds in prod too. */
  isDev: boolean;
  /** Role flags for the user's access record in the current business group. */
  isFinanceAdmin: boolean;
  isBudgetLeader: boolean;
  isBudgetOwner: boolean;
  /** Cost centers assigned to the user in the current business group. */
  authorizedCostCenters: readonly string[];
}

/**
 * xVT is HDW-only in every stage. Dev stays developer-only. Alpha, beta, and prod use normal
 * access management: Finance Admin, Budget Leader, or Budget Owner plus access to either
 * xVT-owned cost center (7802 or 7113).
 */
export const canAccessXvt = ({
  currentBusinessGroupCode,
  isDev,
  isFinanceAdmin = false,
  isBudgetLeader = false,
  isBudgetOwner = false,
  authorizedCostCenters = []
}: XvtAccessParams): boolean => {
  if (currentBusinessGroupCode !== BUSINESS_GROUP_CODES.OPD_HARDWARE) {
    return false;
  }

  const stage = ENVIRONMENT_VARIABLES.env.Stage;
  if (stage !== STAGES.ALPHA && stage !== STAGES.BETA && stage !== STAGES.PROD) {
    return isDev;
  }

  const hasEligibleRole = isFinanceAdmin || isBudgetLeader || isBudgetOwner;
  const hasXvtCostCenterAccess = authorizedCostCenters.some((costCenter) => XVT_COST_CENTERS.includes(costCenter));
  return hasEligibleRole && hasXvtCostCenterAccess;
};
